import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  APPOINTMENT_STATUS_OPTIONS,
  LAST_UPDATE_PRESETS,
  formatDateDisplay,
  formatMileage,
  getDateRangeForPreset,
  getStatusColor,
} from "./formatters";
import { useAppointments } from "../../context/AppointmentsContext";
import useDebounce from "../../../../../hooks/useDebounce";

export const useAppointmentsPage = () => {
  // ============================================================================
  // External Hooks
  // ============================================================================
  const { contactId } = useParams();
  const {
    baseAppointments,
    baseMeta,
    appointments,
    appointmentsMeta,
    advisors,
    vehicles,
    loading,
    filtersLoading,
    error,
    isEmpty,
    fetchAppointments,
    fetchAdvisors,
    fetchVehicles,
    resetAppointmentsToBase,
    resetAppointments,
  } = useAppointments();

  // ============================================================================
  // State - Filter States
  // ============================================================================
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [advisorFilter, setAdvisorFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [lastUpdatePreset, setLastUpdatePreset] = useState("");
  const [lastUpdateRange, setLastUpdateRange] = useState({ from: null, to: null });

  // ============================================================================
  // Debounced Search Value
  // ============================================================================
  const debouncedSearchValue = useDebounce(searchValue, 500);

  // ============================================================================
  // State - Pagination & Sorting
  // ============================================================================
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState(["createdAt"]);
  const [orderDirection, setOrderDirection] = useState(["DESC"]);

  // ============================================================================
  // State - UI State (Keys and Local Searches)
  // ============================================================================
  const [searchKey, setSearchKey] = useState(0);
  const [filtersKey, setFiltersKey] = useState(0);
  const [localSearches, setLocalSearches] = useState({
    status: "",
    advisor: "",
    lastUpdate: "",
    vehicle: "",
  });

  // ============================================================================
  // State - Internal State
  // ============================================================================
  const [hasLoadedBase, setHasLoadedBase] = useState(false);
  const prevContactIdRef = useRef(null);
  const prevSanitizedSearchRef = useRef(null);

  // ============================================================================
  // Helper Functions
  // ============================================================================
  const filterOptions = useCallback((options, searchTerm) => {
    if (!searchTerm.trim()) return options;
    const searchLower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchLower) ||
        option.value?.toLowerCase().includes(searchLower)
    );
  }, []);

  // ============================================================================
  // useMemo - Base Options
  // ============================================================================
  const advisorOptions = useMemo(
    () =>
      advisors.map(
        ({ advisorUuid, advisor_uuid, advisorName, advisor_name }) => ({
          label: advisorName ?? advisor_name ?? "",
          value: advisorUuid ?? advisor_uuid ?? "",
        })
      ),
    [advisors]
  );

  const vehicleOptions = useMemo(
    () =>
      vehicles.map(({ make, model }) => ({
        label: `${make} ${model}`,
        value: `${make} ${model}`,
        meta: { make, model },
      })),
    [vehicles]
  );

  // ============================================================================
  // useMemo - Filtered Options (for Select.Search)
  // ============================================================================
  const filteredStatus = useMemo(
    () => filterOptions(APPOINTMENT_STATUS_OPTIONS, localSearches.status),
    [localSearches.status, filterOptions]
  );

  const filteredAdvisor = useMemo(
    () => filterOptions(advisorOptions, localSearches.advisor),
    [advisorOptions, localSearches.advisor, filterOptions]
  );

  const filteredLastUpdate = useMemo(
    () => filterOptions(LAST_UPDATE_PRESETS, localSearches.lastUpdate),
    [localSearches.lastUpdate, filterOptions]
  );

  const filteredVehicle = useMemo(
    () => filterOptions(vehicleOptions, localSearches.vehicle),
    [vehicleOptions, localSearches.vehicle, filterOptions]
  );

  // ============================================================================
  // useMemo - Derived Data (for API calls)
  // ============================================================================
  const filtersPayload = useMemo(() => {
    const payload = {};

    if (statusFilter) {
      payload.status = statusFilter;
    }

    if (advisorFilter) {
      payload.advisorUuid = advisorFilter;
    }

    if (vehicleFilter) {
      payload.vehicle = vehicleFilter;
    }

    if (lastUpdateRange.from || lastUpdateRange.to) {
      payload.lastUpdate = {
        ...(lastUpdateRange.from ? { from: lastUpdateRange.from } : {}),
        ...(lastUpdateRange.to ? { to: lastUpdateRange.to } : {}),
      };
    }

    return Object.keys(payload).length ? payload : undefined;
  }, [statusFilter, advisorFilter, vehicleFilter, lastUpdateRange]);

  const sanitizedSearch = useMemo(() => {
    const trimmed = debouncedSearchValue.trim();
    return trimmed.length ? trimmed : null;
  }, [debouncedSearchValue]);

  // ============================================================================
  // useMemo - Check if there are active filters
  // ============================================================================
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      sanitizedSearch ||
      statusFilter ||
      advisorFilter ||
      vehicleFilter ||
      lastUpdatePreset
    );
  }, [sanitizedSearch, statusFilter, advisorFilter, vehicleFilter, lastUpdatePreset]);

  // ============================================================================
  // useMemo - Data Transformations
  // ============================================================================
  const mappedAppointments = useMemo(
    () =>
      appointments.map((appointment) => {
        const vehicle = appointment.vehicle || {};
        const vehicleName =
          vehicle.modelYear && vehicle.make && vehicle.model
            ? `${vehicle.modelYear} ${vehicle.make} ${vehicle.model}`
            : vehicle.model
            ? `${vehicle.make || ""} ${vehicle.model}`.trim()
            : vehicle.make || "—";

        const appointmentTimestamp = formatDateDisplay(
          appointment.appointmentDate,
          "hh:mm A · MM/DD/YY"
        );
        const createdVia = [
          vehicle.createdVia || "",
          formatDateDisplay(vehicle.createdDate, "MMM DD, YYYY"),
        ]
          .filter(Boolean)
          .join(" · ");

        const serviceMileage = formatMileage(vehicle.serviceMileage);
        const statusText = appointment.statusDescription || appointment.statusCode || "—";
        const statusColor = getStatusColor(appointment.statusCode);

        return {
          id: appointment.appointmentUuid,
          appointmentUuid: appointment.appointmentUuid,
          vehicle: vehicleName || "—",
          vin: vehicle.vin || "—",
          serviceMileage: serviceMileage || "—",
          advisor: appointment.advisorName || "—",
          status: statusText,
          statusColor,
          createdVia: createdVia || "—",
          timestamp: appointmentTimestamp || "—",
          lastUpdate: formatDateDisplay(appointment.lastUpdate, "MMM DD, YYYY HH:mm"),
          raw: appointment,
        };
      }),
    [appointments]
  );

  // ============================================================================
  // Utility Callbacks
  // ============================================================================
  const resetLocalState = useCallback(() => {
    setSearchValue("");
    setStatusFilter("");
    setAdvisorFilter("");
    setVehicleFilter("");
    setLastUpdatePreset("");
    setLastUpdateRange({ from: null, to: null });
    setPage(1);
    setPageSize(20);
    setOrderBy(["createdAt"]);
    setOrderDirection(["DESC"]);
  }, []);

  const resetLocalSearches = useCallback(() => {
    setLocalSearches({
      status: "",
      advisor: "",
      lastUpdate: "",
      vehicle: "",
    });
  }, []);

  const updateLocalSearch = useCallback((filterType, value) => {
    setLocalSearches((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  }, []);

  // ============================================================================
  // useEffect Hooks
  // ============================================================================
  useEffect(() => {
    if (!contactId) return;

    if (prevContactIdRef.current && prevContactIdRef.current !== contactId) {
      resetAppointments();
    }

    prevContactIdRef.current = contactId;
    setHasLoadedBase(false);
    resetLocalState();
    fetchAdvisors(contactId);
    fetchVehicles(contactId);
  }, [contactId, fetchAdvisors, fetchVehicles, resetAppointments, resetLocalState]);

  useEffect(() => {
    if (!contactId) return;

    const hasFilters = Boolean(filtersPayload || sanitizedSearch);
    const hasSorting =
      (orderBy[0] || "") !== "createdAt" || (orderDirection[0] || "") !== "DESC";
    const hasPaginationChanges = page !== 1 || pageSize !== 20;

    // Detectar cuando el search cambia de tener valor a ser null
    const searchWasCleared = 
      prevSanitizedSearchRef.current !== null && 
      sanitizedSearch === null;

    const params = {
      party_uuid: contactId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch,
    };

    if (!hasFilters && !hasSorting && !hasPaginationChanges) {
      if (!hasLoadedBase) {
        if (baseAppointments.length) {
          resetAppointmentsToBase();
          setHasLoadedBase(true);
          prevSanitizedSearchRef.current = sanitizedSearch;
          return;
        }

        fetchAppointments(params, { persistAsBase: true });
        setHasLoadedBase(true);
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      // Si el search fue borrado y no hay otros filtros, resetear a base
      if (searchWasCleared && !filtersPayload) {
        resetAppointmentsToBase();
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      prevSanitizedSearchRef.current = sanitizedSearch;
      return;
    }

    fetchAppointments(params, { persistAsBase: false });
    prevSanitizedSearchRef.current = sanitizedSearch;
  }, [
    contactId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchAppointments,
    hasLoadedBase,
    baseAppointments.length,
    resetAppointmentsToBase,
  ]);

  // ============================================================================
  // Handlers - Filter Handlers
  // ============================================================================
  const handleStatusChange = useCallback((value) => {    
    setStatusFilter(value || "");
    setPage(1);
  }, []);

  const handleAdvisorChange = useCallback((value) => {    
    setAdvisorFilter(value || "");
    setPage(1);
  }, []);

  const handleVehicleChange = useCallback((value) => {
    setVehicleFilter(value || "");
    setPage(1);
  }, []);

  const handleLastUpdatePresetChange = useCallback((value) => {
    setLastUpdatePreset(value || "");
    setLastUpdateRange(getDateRangeForPreset(value || ""));
    setPage(1);
  }, []);

  // ============================================================================
  // Handlers - Search Handlers
  // ============================================================================
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // ============================================================================
  // Handlers - Clear Handlers
  // ============================================================================
  const handleClearFilters = useCallback(() => {
    setSearchValue("");
    setStatusFilter("");
    setAdvisorFilter("");
    setVehicleFilter("");
    setLastUpdatePreset("");
    setLastUpdateRange({ from: null, to: null });
    setPage(1);
    resetAppointmentsToBase();
  }, [resetAppointmentsToBase]);

  const handleClearFiltersWithKeys = useCallback(() => {
    resetLocalSearches();
    setSearchKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);
    handleClearFilters();
  }, [resetLocalSearches, handleClearFilters]);

  // ============================================================================
  // Handlers - Pagination Handlers
  // ============================================================================
  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);

      if (nextPage === 1) {
        const hasFilters = Boolean(
          statusFilter ||
            advisorFilter ||
            vehicleFilter ||
            lastUpdateRange.from ||
            lastUpdateRange.to ||
            sanitizedSearch
        );
        const isDefaultSorting =
          (orderBy[0] || "") === "createdAt" && (orderDirection[0] || "") === "DESC";

        if (!hasFilters && isDefaultSorting) {
          resetAppointmentsToBase();
        }
      }
    },
    [
      statusFilter,
      advisorFilter,
      vehicleFilter,
      lastUpdateRange.from,
      lastUpdateRange.to,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetAppointmentsToBase,
    ]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage) => {
      setPageSize(itemsPerPage);
      setPage(1);

      if (itemsPerPage === 20) {
        const hasFilters = Boolean(
          statusFilter ||
            advisorFilter ||
            vehicleFilter ||
            lastUpdateRange.from ||
            lastUpdateRange.to ||
            sanitizedSearch
        );
        const isDefaultSorting =
          (orderBy[0] || "") === "createdAt" && (orderDirection[0] || "") === "DESC";

        if (!hasFilters && isDefaultSorting) {
          resetAppointmentsToBase();
        }
      }
    },
    [
      statusFilter,
      advisorFilter,
      vehicleFilter,
      lastUpdateRange.from,
      lastUpdateRange.to,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetAppointmentsToBase,
    ]
  );

  // ============================================================================
  // Handlers - Refresh Handler
  // ============================================================================
  const handleRefresh = useCallback(() => {
    if (!contactId) return;
    
    const params = {
      party_uuid: contactId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch,
    };
    
    fetchAppointments(params);
  }, [
    contactId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchAppointments,
  ]);

  // ============================================================================
  // Return
  // ============================================================================
  return {
    loading,
    filtersLoading,
    error,
    isEmpty,
    appointments: mappedAppointments,
    appointmentsMeta,
    filters: {
      statusOptions: APPOINTMENT_STATUS_OPTIONS,
      advisorOptions,
      vehicleOptions,
      lastUpdateOptions: LAST_UPDATE_PRESETS,
      filteredStatus,
      filteredAdvisor,
      filteredLastUpdate,
      filteredVehicle,
      selectedStatus: statusFilter,
      selectedAdvisor: advisorFilter,
      selectedVehicle: vehicleFilter,
      selectedLastUpdatePreset: lastUpdatePreset,
      searchValue,
      hasActiveFilters,
    },
    filterHandlers: {
      handleStatusChange,
      handleAdvisorChange,
      handleVehicleChange,
      handleLastUpdatePresetChange,
      handleSearchChange,
      handleClearFiltersWithKeys,
    },
    filterUI: {
      searchKey,
      filtersKey,
      updateLocalSearch,
    },
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
  };
};
